import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Save, RefreshCw, FileText, Bold, Italic, List, Link as LinkIcon, Type, Palette, RotateCcw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PageContentEditor = ({ getAuthHeaders }) => {
  const [selectedPage, setSelectedPage] = useState("landing");
  const [selectedSection, setSelectedSection] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [textColor, setTextColor] = useState("#ffffff");
  const [bgColor, setBgColor] = useState("#000000");
  
  // Color history - last 4 colors used (persisted to localStorage)
  const [textColorHistory, setTextColorHistory] = useState(() => {
    try {
      const saved = localStorage.getItem("cms_text_color_history");
      return saved ? JSON.parse(saved) : ["#ffffff", "#22c55e", "#3b82f6", "#f59e0b"];
    } catch { return ["#ffffff", "#22c55e", "#3b82f6", "#f59e0b"]; }
  });
  const [bgColorHistory, setBgColorHistory] = useState(() => {
    try {
      const saved = localStorage.getItem("cms_bg_color_history");
      return saved ? JSON.parse(saved) : ["#000000", "#1e293b", "#1e40af", "#7c2d12"];
    } catch { return ["#000000", "#1e293b", "#1e40af", "#7c2d12"]; }
  });

  // Helper to add color to history
  const addToColorHistory = (color, history, setHistory, storageKey) => {
    const newHistory = [color, ...history.filter(c => c !== color)].slice(0, 4);
    setHistory(newHistory);
    localStorage.setItem(storageKey, JSON.stringify(newHistory));
  };

  // Define page sections with default values
  const pageSections = {
    landing: [
      { id: "hero-badge", label: "Hero Badge Text", defaultValue: "12 Database Types • No Long-Term Commitment" },
      { id: "hero-title", label: "Hero Section - Title", defaultValue: "Manage All Your Databases from One Powerful Platform" },
      { id: "hero-subtitle", label: "Hero Section - Subtitle", defaultValue: "Connect to MS SQL Server, MySQL, PostgreSQL, MongoDB, and 8+ other databases. Run queries, explore schemas, save favorite queries, and leverage AI-powered assistance—all in one intuitive web interface." },
      { id: "hero-cta-primary", label: "Hero Primary CTA Button", defaultValue: "Start Free Trial" },
      { id: "hero-cta-secondary", label: "Hero Secondary CTA Button", defaultValue: "Sign In" },
      { id: "hero-footnote", label: "Hero Footnote", defaultValue: "No credit card required • Cancel anytime" },
      { id: "features-title", label: "Features Section - Title", defaultValue: "Powerful Features for Every Database" },
      { id: "features-subtitle", label: "Features Section - Subtitle", defaultValue: "Everything you need to manage SQL and NoSQL databases efficiently" },
      { id: "pricing-title", label: "Pricing Section - Title", defaultValue: "Simple, Credit-Based Pricing" },
      { id: "pricing-subtitle", label: "Pricing Section - Subtitle", defaultValue: "Start with full access to all features" },
      { id: "support-title", label: "Support Section - Title", defaultValue: "Support & FAQ" },
      { id: "support-subtitle", label: "Support Section - Subtitle", defaultValue: "Get answers to common questions or chat with our AI assistant" },
      { id: "support-chat-title", label: "Support Chat - Title", defaultValue: "Need More Help?" },
      { id: "support-chat-subtitle", label: "Support Chat - Subtitle", defaultValue: "Chat with our AI assistant for instant answers to your questions" },
      { id: "cta-title", label: "CTA Section - Title", defaultValue: "Ready to Get Started?" },
      { id: "cta-subtitle", label: "CTA Section - Subtitle", defaultValue: "Join thousands of developers and DBAs who trust AIQueryStudio for their database management needs." },
      { id: "cta-primary-btn", label: "CTA Primary Button", defaultValue: "Create Free Account" },
      { id: "cta-secondary-btn", label: "CTA Secondary Button", defaultValue: "Ask a Question" },
      { id: "cta-footnote", label: "CTA Footnote", defaultValue: "No credit card required • No long-term commitment • Cancel anytime" },
    ],
    about: [
      { id: "hero-title", label: "Hero Section - Title", defaultValue: "Professional Database Management" },
      { id: "hero-subtitle", label: "Hero Section - Subtitle", defaultValue: "AIQueryStudio is a comprehensive database management platform..." },
      { id: "seo-description", label: "SEO Meta Description", defaultValue: "AIQueryStudio is a professional database management tool..." },
      { id: "features-intro", label: "Features Introduction", defaultValue: "Discover the powerful features that make AIQueryStudio the choice of professionals" },
      { id: "why-choose-title", label: "Why Choose Us - Title", defaultValue: "Why Choose AIQueryStudio?" },
      { id: "cta-title", label: "CTA Section - Title", defaultValue: "Ready to Get Started?" },
      { id: "cta-subtitle", label: "CTA Section - Subtitle", defaultValue: "Join thousands of developers and DBAs who trust AIQueryStudio." },
    ],
    privacy: [
      { id: "introduction", label: "Introduction", defaultValue: "Your privacy is important to us..." },
      { id: "info-collection", label: "Information We Collect", defaultValue: "We collect information you provide directly..." },
      { id: "info-usage", label: "How We Use Your Information", defaultValue: "We use the information we collect to..." },
      { id: "data-security", label: "Data Security", defaultValue: "We implement appropriate security measures..." },
      { id: "data-retention", label: "Data Retention", defaultValue: "We retain your data for as long as necessary..." },
      { id: "user-rights", label: "Your Rights", defaultValue: "You have the right to access, correct, or delete your data..." },
      { id: "cookies", label: "Cookies", defaultValue: "We use cookies to improve your experience..." },
      { id: "third-party", label: "Third-Party Services", defaultValue: "We may use third-party services..." },
      { id: "changes", label: "Changes to Privacy Policy", defaultValue: "We may update this policy from time to time..." },
      { id: "contact", label: "Contact Us", defaultValue: "If you have any questions about this privacy policy..." },
    ],
  };

  // Get default value for current section
  const getDefaultValue = () => {
    const section = pageSections[selectedPage]?.find(s => s.id === selectedSection);
    return section?.defaultValue || "";
  };

  // Load content when page or section changes
  useEffect(() => {
    if (selectedPage && selectedSection) {
      loadContent();
    }
  }, [selectedPage, selectedSection]);

  // Set first section when page changes
  useEffect(() => {
    if (selectedPage && pageSections[selectedPage]?.length > 0) {
      setSelectedSection(pageSections[selectedPage][0].id);
    }
  }, [selectedPage]);

  const loadContent = async () => {
    if (!selectedSection) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/page-content/${selectedPage}/${selectedSection}`
      );
      const loadedContent = response.data.content || "";
      // If content is empty or is the default placeholder, use the default value
      if (!loadedContent || response.data.is_default) {
        setContent(getDefaultValue());
      } else {
        setContent(loadedContent);
      }
    } catch (error) {
      // If no content exists (404), use default value
      if (error.response?.status === 404) {
        setContent(getDefaultValue());
      } else {
        console.error("Error loading content:", error);
        toast.error("Failed to load content");
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset content to default value
  const resetToDefault = () => {
    const defaultValue = getDefaultValue();
    if (defaultValue) {
      setContent(defaultValue);
      toast.info("Reset to default value");
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/page-content/${selectedPage}/${selectedSection}`,
        { content },
        { headers: getAuthHeaders() }
      );
      toast.success("Content saved successfully");
    } catch (error) {
      console.error("Error saving content:", error);
      toast.error(error.response?.data?.detail || "Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const getSectionLabel = () => {
    const section = pageSections[selectedPage]?.find(s => s.id === selectedSection);
    return section?.label || selectedSection;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Page Content Editor
          </CardTitle>
          <CardDescription>
            Edit website content using a rich text editor. Changes will be reflected immediately on the live site.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Page and Section Selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Select Page</Label>
              <Select value={selectedPage} onValueChange={setSelectedPage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landing">Landing Page</SelectItem>
                  <SelectItem value="about">About Page</SelectItem>
                  <SelectItem value="privacy">Privacy Page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Section</Label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSections[selectedPage]?.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Editor */}
          {selectedSection && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Editing: {getSectionLabel()}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetToDefault}
                    disabled={loading}
                    className="gap-2 text-muted-foreground"
                    title="Reset to default value"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadContent}
                    disabled={loading}
                    className="gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveContent}
                    disabled={saving || loading}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>

              {/* Enhanced Text Editor with HTML support */}
              <div className="space-y-2">
                {/* Formatting Toolbar */}
                <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-lg border border-border">
                  {/* Heading Controls */}
                  <div className="flex gap-1 border-r border-border pr-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const selection = window.getSelection().toString();
                        if (selection) {
                          const h1Text = `<h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">${selection}</h1>`;
                          setContent(content.replace(selection, h1Text));
                        } else {
                          setContent(content + '\n<h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">Heading 1</h1>');
                        }
                      }}
                      title="Heading 1 (H1)"
                      className="font-bold"
                    >
                      H1
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const selection = window.getSelection().toString();
                        if (selection) {
                          const h2Text = `<h2 class="text-3xl sm:text-4xl font-bold mb-4">${selection}</h2>`;
                          setContent(content.replace(selection, h2Text));
                        } else {
                          setContent(content + '\n<h2 class="text-3xl sm:text-4xl font-bold mb-4">Heading 2</h2>');
                        }
                      }}
                      title="Heading 2 (H2)"
                      className="font-bold"
                    >
                      H2
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const selection = window.getSelection().toString();
                        if (selection) {
                          const h3Text = `<h3 class="text-2xl font-semibold mb-3">${selection}</h3>`;
                          setContent(content.replace(selection, h3Text));
                        } else {
                          setContent(content + '\n<h3 class="text-2xl font-semibold mb-3">Heading 3</h3>');
                        }
                      }}
                      title="Heading 3 (H3)"
                      className="font-bold"
                    >
                      H3
                    </Button>
                  </div>

                  {/* Text Formatting */}
                  <div className="flex gap-1 border-r border-border pr-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const selection = window.getSelection().toString();
                        if (selection) {
                          const boldText = `<strong>${selection}</strong>`;
                          setContent(content.replace(selection, boldText));
                        }
                      }}
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const selection = window.getSelection().toString();
                        if (selection) {
                          const italicText = `<em>${selection}</em>`;
                          setContent(content.replace(selection, italicText));
                        }
                      }}
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* List and Link */}
                  <div className="flex gap-1 border-r border-border pr-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const listItem = prompt("Enter list item:");
                        if (listItem) {
                          setContent(content + `\n<li class="mb-2">${listItem}</li>`);
                        }
                      }}
                      title="Add List Item"
                    >
                      <List className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const url = prompt("Enter URL:");
                        const text = prompt("Enter link text:");
                        if (url && text) {
                          setContent(content + `<a href="${url}" class="text-primary hover:underline">${text}</a>`);
                        }
                      }}
                      title="Add Link"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Color Controls */}
                  <div className="flex flex-wrap gap-3 items-center border-l border-border pl-2">
                    {/* Text Color */}
                    <div className="flex items-center gap-1.5">
                      <Palette className="w-4 h-4 text-muted-foreground" />
                      <Label htmlFor="textColor" className="text-xs">Text:</Label>
                      <Input
                        id="textColor"
                        type="color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        className="w-10 h-7 p-0.5 cursor-pointer"
                        title="Text Color"
                      />
                      {/* Text Color History */}
                      <div className="flex gap-0.5">
                        {textColorHistory.map((color, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-6 h-6 rounded border border-border hover:scale-110 transition-transform"
                            style={{ backgroundColor: color }}
                            onClick={() => setTextColor(color)}
                            title={`Use ${color}`}
                          />
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          const selection = window.getSelection().toString();
                          if (selection) {
                            const coloredText = `<span style="color: ${textColor}">${selection}</span>`;
                            setContent(content.replace(selection, coloredText));
                            addToColorHistory(textColor, textColorHistory, setTextColorHistory, "cms_text_color_history");
                          } else {
                            toast.info("Select text first, then click to apply color");
                          }
                        }}
                        title="Apply text color to selection"
                      >
                        Apply
                      </Button>
                    </div>

                    {/* Background Color */}
                    <div className="flex items-center gap-1.5">
                      <Type className="w-4 h-4 text-muted-foreground" />
                      <Label htmlFor="bgColor" className="text-xs">BG:</Label>
                      <Input
                        id="bgColor"
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-10 h-7 p-0.5 cursor-pointer"
                        title="Background Color"
                      />
                      {/* BG Color History */}
                      <div className="flex gap-0.5">
                        {bgColorHistory.map((color, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-6 h-6 rounded border border-border hover:scale-110 transition-transform"
                            style={{ backgroundColor: color }}
                            onClick={() => setBgColor(color)}
                            title={`Use ${color}`}
                          />
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          const selection = window.getSelection().toString();
                          if (selection) {
                            const bgText = `<span style="background-color: ${bgColor}; padding: 2px 6px; border-radius: 4px;">${selection}</span>`;
                            setContent(content.replace(selection, bgText));
                            addToColorHistory(bgColor, bgColorHistory, setBgColorHistory, "cms_bg_color_history");
                          } else {
                            toast.info("Select text first, then click to apply background");
                          }
                        }}
                        title="Apply background color to selection"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Textarea Editor */}
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter HTML content here... Use the buttons above for formatting or write HTML directly."
                  rows={15}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  💡 Select text in the editor and click formatting buttons to apply styles. 
                  You can also use HTML tags directly (e.g., &lt;h1&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;span&gt;)
                </p>
              </div>

              {/* Preview */}
              {content && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div
                    className="p-4 border border-border rounded-lg bg-card prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              )}
            </div>
          )}

          {!selectedSection && (
            <div className="text-center py-8 text-muted-foreground">
              Select a page and section to start editing
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Formatting Guide</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <div>
            <h4 className="font-semibold text-foreground mb-1">How to Use:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Select a page and section from the dropdowns above</li>
              <li>The editor will auto-populate with existing content</li>
              <li><strong>Select text in the editor</strong> and click formatting buttons to apply styles</li>
              <li>Or write HTML directly in the textarea</li>
              <li>Click "Save" to apply changes to the live website</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-foreground mb-1">Formatting Options:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>H1, H2, H3:</strong> Create headings (includes default Tailwind styling)</li>
              <li><strong>Bold/Italic:</strong> Wrap selected text in &lt;strong&gt; or &lt;em&gt; tags</li>
              <li><strong>Text Color:</strong> Select text → Pick color → Click "Apply"</li>
              <li><strong>Background:</strong> Select text → Pick BG color → Click "Apply"</li>
              <li><strong>List:</strong> Add list items with proper styling</li>
              <li><strong>Link:</strong> Create clickable links with hover effects</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-1">Default Styling:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>H1: Large, bold, responsive (text-4xl → text-6xl)</li>
              <li>H2: Medium-large, bold (text-3xl → text-4xl)</li>
              <li>H3: Medium, semibold (text-2xl)</li>
              <li>All headings include proper margins and tracking</li>
              <li>Links include primary color and hover underline</li>
              <li>Styles are preserved across the website</li>
            </ul>
          </div>

          <div className="pt-2 border-t border-border">
            <p className="text-xs">
              <strong>Pro Tip:</strong> You can also edit the HTML directly to fine-tune spacing, classes, or add custom inline styles!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageContentEditor;
